import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingBag, Loader2, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useCart, itemKey } from "@/context/CartContext";
import { fmtINR } from "@/data/catalog";
import { submitQuote } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, clear } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", city: "", message: "" });
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteDone, setQuoteDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const requestQuote = async (e) => {
    e.preventDefault();
    setQuoteBusy(true);
    const summary = items.map((i) => `${i.name} (${[i.size, i.color].filter(Boolean).join("/")}) x${i.quantity}`).join("; ");
    try {
      await submitQuote({ ...form, product: "Cart Enquiry", quantity: `${items.reduce((s, i) => s + i.quantity, 0)} units`, message: `Cart: ${summary}. ${form.message}` });
      setQuoteDone(true); clear();
      toast.success("Quote request sent! Our team will reach out shortly.");
    } catch { toast.error("Could not submit. Please try again."); }
    finally { setQuoteBusy(false); }
  };

  const inputCls = "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-royal";

  if (items.length === 0 && !quoteDone) {
    return (
      <div data-testid="cart-page">
        <PageHeader overline="Cart" title="Your cart is empty." />
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10 text-center">
          <ShoppingBag size={48} className="mx-auto text-muted-foreground" />
          <Link to="/products" className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy text-white px-7 py-3.5 font-semibold" data-testid="cart-browse">Browse products <ArrowRight size={18} /></Link>
        </div>
      </div>
    );
  }

  if (quoteDone) {
    return (
      <div data-testid="cart-quote-success">
        <PageHeader overline="Thank you" title="Quote request received!" subtitle="Our packaging specialists will review your cart and send a tailored wholesale quotation within one business day." />
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10"><Link to="/products" className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-7 py-3.5 font-semibold">Continue browsing <ArrowRight size={18} /></Link></div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page">
      <PageHeader overline="Your Cart" title="Review your items & request a quote." />
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
        <div className="space-y-4">
          {items.map((i) => {
            const k = itemKey(i);
            return (
              <div key={k} className="flex gap-4 rounded-3xl border border-border bg-card p-4" data-testid={`cart-row-${i.slug}`}>
                <img src={i.image} alt={i.name} className="h-24 w-24 rounded-2xl object-cover border border-border" />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-3">
                    <div><h3 className="font-heading font-bold text-foreground">{i.name}</h3><p className="text-xs text-muted-foreground">{[i.size, i.color].filter(Boolean).join(" · ")}</p></div>
                    <button onClick={() => removeItem(k)} className="text-muted-foreground hover:text-destructive" data-testid={`row-remove-${i.slug}`}><Trash2 size={17} /></button>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center border border-border rounded-full">
                      <button onClick={() => updateQty(k, i.quantity - 10)} className="grid place-items-center h-9 w-9" data-testid={`row-dec-${i.slug}`}><Minus size={15} /></button>
                      <span className="w-12 text-center text-sm font-semibold">{i.quantity}</span>
                      <button onClick={() => updateQty(k, i.quantity + 10)} className="grid place-items-center h-9 w-9" data-testid={`row-inc-${i.slug}`}><Plus size={15} /></button>
                    </div>
                    <span className="font-mono-accent font-semibold text-foreground">{fmtINR(i.price * i.quantity)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="lg:sticky lg:top-28 space-y-6">
          <div className="rounded-3xl border border-border bg-card p-6">
            <div className="flex justify-between items-center"><span className="text-muted-foreground">Estimated Subtotal</span><span className="font-heading text-2xl font-extrabold text-foreground" data-testid="cart-total">{fmtINR(subtotal)}</span></div>
            <p className="mt-1 text-xs text-muted-foreground">Request a quote below for exact pricing, bulk discounts, and shipping details.</p>
          </div>

          <div className="rounded-3xl border border-border bg-card p-6">
            <h3 className="font-heading text-lg font-bold text-foreground">Prefer a bulk quote?</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Send your cart as an enquiry for the best wholesale pricing.</p>
            <form onSubmit={requestQuote} className="space-y-3" data-testid="cart-quote-form">
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="cq-name" />
                <input placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} data-testid="cq-company" />
                <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="cq-email" />
                <input required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="cq-phone" />
              </div>
              <textarea rows={2} placeholder="Notes (optional)" value={form.message} onChange={set("message")} className={inputCls} data-testid="cq-message" />
              <button type="submit" disabled={quoteBusy} data-testid="cart-request-quote" className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-border px-6 py-3.5 font-semibold hover:bg-secondary transition-colors disabled:opacity-60">
                {quoteBusy ? <Loader2 size={18} className="animate-spin" /> : "Request Quote for Cart"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
